import { useMemo } from 'react'
import { CellPlotData } from 'redux/slices'
import { PlotMarker } from 'plotly.js'
import { DENSITY_ATTRIBUTE } from 'utils/constants'

const usePlotDataWithScale = (
  plotData: CellPlotData[],
  plotDisplayType: string,
  showPreFilter: boolean,
  dragDropEnabled = false
): CellPlotData[] => {
  const plotDataWithScale = useMemo(() => {
    if (plotData?.length > 0 && plotDisplayType === DENSITY_ATTRIBUTE && !showPreFilter) {
      // Use structuredClone instead of JSON.parse/stringify for better performance
      const updatedData = structuredClone(plotData)
      // Initialize max and min color values
      let maxColor = -Infinity
      let minColor = Infinity

      // First pass: find min/max values
      updatedData.forEach((data: CellPlotData) => {
        const colorData = data?.marker?.color
        // Only process if colorData is an array (skip centroid traces and other non-array markers)
        if (Array.isArray(colorData) && colorData.length > 0) {
          const numericData = colorData.map(Number).filter(n => !Number.isNaN(n))
          if (numericData.length > 0) {
            const maxDataColor = Math.max(...numericData)
            const minDataColor = Math.min(...numericData)
            maxColor = Math.max(maxColor, maxDataColor)
            minColor = Math.min(minColor, minDataColor)
          }
        }
      })

      // Second pass: update showscale and colorbar
      updatedData.forEach((data: CellPlotData) => {
        const plotMarker = data?.marker as PlotMarker & { cmin?: number; cmax?: number }
        if (plotMarker) {
          if (dragDropEnabled) {
            // Completely hide colorbar when drag-drop is enabled
            plotMarker.showscale = false
            // Remove colorbar configuration to ensure it doesn't show
            delete plotMarker.colorbar
            // Also remove cmin/cmax which might force colorbar to show
            delete plotMarker.cmin
            delete plotMarker.cmax
          } else {
            // Show colorbar with min/max labels
            plotMarker.showscale = true
            plotMarker.colorbar = {
              ...plotMarker.colorbar,
              tickvals: [maxColor, minColor],
              ticktext: ['Max', 'Min'],
            }
          }
        }
      })
      return updatedData
    }
    return plotData
  }, [plotData, plotDisplayType, showPreFilter, dragDropEnabled])

  return plotDataWithScale
}

export default usePlotDataWithScale
